/**
 * IndexedDB Ticket Repository Implementation
 *
 * Implements ITicketRepository using Dexie.js for IndexedDB storage.
 */

import { db } from '../../db';
import type { ITicketRepository } from '../interfaces/ITicketRepository';
import type {
  Ticket,
  IncidentTicket,
  RequestTicket,
  ProblemTicket,
  ChangeTicket,
  TicketStatus,
  TicketType,
  Priority,
  Location,
  JobTag,
  TicketCategoryType,
} from '../../../types/ticket';
import {
  TicketStatus as TicketStatusEnum,
  TicketType as TicketTypeEnum,
  AssignmentGroup,
  ApprovalStatus,
  getTicketCategory,
} from '../../../types/ticket';
import type {
  CreateTicketDTO,
  CreateIncidentTicketDTO,
  CreateRequestTicketDTO,
  CreateProblemTicketDTO,
  CreateChangeTicketDTO,
  UpdateTicketDTO,
} from '../../../types/dto';

export class IndexedDBTicketRepository implements ITicketRepository {
  async getAll(): Promise<Ticket[]> {
    return db.tickets.toArray();
  }

  async getById(id: string): Promise<Ticket | null> {
    const ticket = await db.tickets.get(id);
    return ticket ?? null;
  }

  async getByTicketNumber(ticketNumber: string): Promise<Ticket | null> {
    const ticket = await db.tickets.where('ticketNumber').equals(ticketNumber).first();
    return ticket ?? null;
  }

  async create(data: CreateTicketDTO): Promise<Ticket> {
    const now = new Date();

    // Base fields common to all ticket types
    const baseFields = {
      id: crypto.randomUUID(),
      ticketNumber: data.ticketNumber,
      shortDescription: data.shortDescription,
      priority: data.priority,
      status: TicketStatusEnum.NEW,
      location: data.location,
      assignedTechId: null,
      requesterId: data.requesterId,
      requesterName: data.requesterName,
      requesterEmail: data.requesterEmail,
      requesterDepartment: data.requesterDepartment,
      createdBy: data.createdBy,
      createdByName: data.createdByName,
      createdAt: now,
      updatedAt: now,
      slaDeadline: this.calculateSlaDeadline(data.priority, now),
      notes: data.notes,
      tags: data.tags,
      assignmentGroup: AssignmentGroup.SERVICE_DESK,
    };

    let ticket: Ticket;

    // Build type-specific ticket
    switch (data.ticketType) {
      case TicketTypeEnum.INCIDENT: {
        const incidentData = data as CreateIncidentTicketDTO;
        ticket = {
          ...baseFields,
          ticketType: TicketTypeEnum.INCIDENT,
          category: incidentData.category,
          subcategory: incidentData.subcategory,
        } as IncidentTicket;
        break;
      }

      case TicketTypeEnum.REQUEST: {
        const requestData = data as CreateRequestTicketDTO;
        ticket = {
          ...baseFields,
          ticketType: TicketTypeEnum.REQUEST,
          category: requestData.category,
          subcategory: requestData.subcategory,
          approvalStatus: requestData.approvalStatus ?? ApprovalStatus.NOT_REQUIRED,
          neededByDate: requestData.neededByDate,
          requestedItemName: requestData.requestedItemName,
        } as RequestTicket;
        break;
      }

      case TicketTypeEnum.PROBLEM: {
        const problemData = data as CreateProblemTicketDTO;
        ticket = {
          ...baseFields,
          ticketType: TicketTypeEnum.PROBLEM,
          category: problemData.category,
          subcategory: problemData.subcategory,
          problemStatement: problemData.problemStatement,
          rootCause: problemData.rootCause,
          workaround: problemData.workaround,
          linkedIncidentIds: problemData.linkedIncidentIds ?? [],
        } as ProblemTicket;
        break;
      }

      default: {
        // Assume Change ticket for any unhandled type (type system ensures this is ChangeTicket)
        const changeData = data as CreateChangeTicketDTO;
        ticket = {
          ...baseFields,
          ticketType: TicketTypeEnum.CHANGE,
          changeCategory: changeData.changeCategory,
          changeType: changeData.changeType,
          riskLevel: changeData.riskLevel,
          approvalStatus: changeData.approvalStatus ?? ApprovalStatus.PENDING,
          rollbackPlan: changeData.rollbackPlan,
          implementationStart: changeData.implementationStart,
          implementationEnd: changeData.implementationEnd,
        } as ChangeTicket;
        break;
      }
    }

    await db.tickets.add(ticket);
    return ticket;
  }

  async update(id: string, data: UpdateTicketDTO): Promise<Ticket | null> {
    const existing = await this.getById(id);
    if (!existing) {
      return null;
    }

    // Build update object with base fields
    const updates: Record<string, unknown> = {
      updatedAt: new Date(),
    };

    // Base fields
    if (data.shortDescription !== undefined) updates.shortDescription = data.shortDescription;
    if (data.ticketType !== undefined) updates.ticketType = data.ticketType;
    if (data.category !== undefined) updates.category = data.category;
    if (data.subcategory !== undefined) updates.subcategory = data.subcategory;
    if (data.priority !== undefined) updates.priority = data.priority;
    if (data.status !== undefined) updates.status = data.status;
    if (data.location !== undefined) updates.location = data.location;
    if (data.assignedTechId !== undefined) updates.assignedTechId = data.assignedTechId ?? undefined;
    if (data.assignedTechName !== undefined) updates.assignedTechName = data.assignedTechName ?? undefined;
    if (data.notes !== undefined) updates.notes = data.notes;
    if (data.slaDeadline !== undefined) updates.slaDeadline = data.slaDeadline ?? undefined;
    if (data.waitingReason !== undefined) updates.waitingReason = data.waitingReason;
    if (data.waitingReasonNote !== undefined) updates.waitingReasonNote = data.waitingReasonNote;
    if (data.assignmentGroup !== undefined) updates.assignmentGroup = data.assignmentGroup;
    if (data.escalationTier !== undefined) updates.escalationTier = data.escalationTier;
    if (data.resolutionNotes !== undefined) updates.resolutionNotes = data.resolutionNotes;
    if (data.firstResponseAt !== undefined) updates.firstResponseAt = data.firstResponseAt;
    if (data.resolvedAt !== undefined) updates.resolvedAt = data.resolvedAt;
    if (data.closedAt !== undefined) updates.closedAt = data.closedAt;
    if (data.tags !== undefined) updates.tags = data.tags;

    // Request-specific fields
    if (data.approvalStatus !== undefined) updates.approvalStatus = data.approvalStatus;
    if (data.neededByDate !== undefined) updates.neededByDate = data.neededByDate;
    if (data.requestedItemName !== undefined) updates.requestedItemName = data.requestedItemName;

    // Problem-specific fields
    if (data.problemStatement !== undefined) updates.problemStatement = data.problemStatement;
    if (data.rootCause !== undefined) updates.rootCause = data.rootCause;
    if (data.workaround !== undefined) updates.workaround = data.workaround;
    if (data.linkedIncidentIds !== undefined) updates.linkedIncidentIds = data.linkedIncidentIds;

    // Change-specific fields
    if (data.changeCategory !== undefined) updates.changeCategory = data.changeCategory;
    if (data.changeType !== undefined) updates.changeType = data.changeType;
    if (data.riskLevel !== undefined) updates.riskLevel = data.riskLevel;
    if (data.rollbackPlan !== undefined) updates.rollbackPlan = data.rollbackPlan;
    if (data.implementationStart !== undefined) updates.implementationStart = data.implementationStart;
    if (data.implementationEnd !== undefined) updates.implementationEnd = data.implementationEnd;

    const updatedTicket = { ...existing, ...updates } as Ticket;

    await db.tickets.put(updatedTicket);
    return updatedTicket;
  }

  async delete(id: string): Promise<boolean> {
    const existing = await this.getById(id);
    if (!existing) {
      return false;
    }
    await db.tickets.delete(id);
    return true;
  }

  async getByTechId(techId: string): Promise<Ticket[]> {
    return db.tickets.where('assignedTechId').equals(techId).toArray();
  }

  async getByStatus(status: TicketStatus): Promise<Ticket[]> {
    return db.tickets.where('status').equals(status).toArray();
  }

  async getByPriority(priority: Priority): Promise<Ticket[]> {
    return db.tickets.where('priority').equals(priority).toArray();
  }

  async getByLocation(location: Location): Promise<Ticket[]> {
    return db.tickets.where('location').equals(location).toArray();
  }

  async getUnassigned(): Promise<Ticket[]> {
    // IndexedDB doesn't index null values well, so we filter in memory
    const all = await this.getAll();
    return all.filter((t) => t.assignedTechId === null);
  }

  async updateStatus(id: string, status: TicketStatus): Promise<Ticket | null> {
    return this.update(id, { status });
  }

  async assignToTech(id: string, techId: string): Promise<Ticket | null> {
    return this.update(id, {
      assignedTechId: techId,
      status: TicketStatusEnum.IN_PROGRESS,
    });
  }

  async unassign(id: string): Promise<Ticket | null> {
    return this.update(id, { assignedTechId: null });
  }

  async search(query: string): Promise<Ticket[]> {
    if (!query.trim()) {
      return this.getAll();
    }

    const lowerQuery = query.toLowerCase();
    const all = await this.getAll();

    return all.filter(
      (ticket) =>
        ticket.ticketNumber.toLowerCase().includes(lowerQuery) ||
        ticket.shortDescription.toLowerCase().includes(lowerQuery) ||
        ticket.notes?.toLowerCase().includes(lowerQuery) ||
        ticket.requesterName?.toLowerCase().includes(lowerQuery) ||
        ticket.requesterEmail?.toLowerCase().includes(lowerQuery)
    );
  }

  async getByTicketType(ticketType: TicketType): Promise<Ticket[]> {
    return db.tickets.where('ticketType').equals(ticketType).toArray();
  }

  async getByCategory(category: TicketCategoryType): Promise<Ticket[]> {
    // Use getTicketCategory helper to handle Change tickets which use changeCategory
    const all = await this.getAll();
    return all.filter((t) => getTicketCategory(t) === category);
  }

  async getByRequesterId(requesterId: string): Promise<Ticket[]> {
    return db.tickets.where('requesterId').equals(requesterId).toArray();
  }

  async getFiltered(filters: {
    statuses?: TicketStatus[];
    priorities?: Priority[];
    locations?: Location[];
    ticketTypes?: TicketType[];
    categories?: TicketCategoryType[];
    assignedTechId?: string | null;
    requesterId?: string;
    searchQuery?: string;
    tags?: JobTag[];
  }): Promise<Ticket[]> {
    let tickets = await this.getAll();

    if (filters.statuses && filters.statuses.length > 0) {
      tickets = tickets.filter((t) => filters.statuses!.includes(t.status));
    }

    if (filters.priorities && filters.priorities.length > 0) {
      tickets = tickets.filter((t) => filters.priorities!.includes(t.priority));
    }

    if (filters.locations && filters.locations.length > 0) {
      tickets = tickets.filter((t) => filters.locations!.includes(t.location));
    }

    if (filters.ticketTypes && filters.ticketTypes.length > 0) {
      tickets = tickets.filter((t) => filters.ticketTypes!.includes(t.ticketType));
    }

    if (filters.categories && filters.categories.length > 0) {
      tickets = tickets.filter((t) => filters.categories!.includes(getTicketCategory(t)));
    }

    // Legacy tags filter (deprecated)
    if (filters.tags && filters.tags.length > 0) {
      tickets = tickets.filter((t) => t.tags && filters.tags!.some((tag) => t.tags!.includes(tag)));
    }

    if (filters.assignedTechId !== undefined) {
      tickets = tickets.filter((t) => t.assignedTechId === filters.assignedTechId);
    }

    if (filters.requesterId) {
      tickets = tickets.filter((t) => t.requesterId === filters.requesterId);
    }

    if (filters.searchQuery && filters.searchQuery.trim()) {
      const query = filters.searchQuery.toLowerCase();
      tickets = tickets.filter(
        (t) =>
          t.ticketNumber.toLowerCase().includes(query) ||
          t.shortDescription.toLowerCase().includes(query) ||
          t.requesterName?.toLowerCase().includes(query) ||
          t.requesterEmail?.toLowerCase().includes(query)
      );
    }

    return tickets;
  }

  /**
   * Calculate SLA deadline based on priority
   */
  private calculateSlaDeadline(priority: Priority, createdAt: Date): Date {
    const hours: Record<Priority, number> = {
      P1: 4,
      P2: 8,
      P3: 24,
      P4: 72,
    };

    const deadline = new Date(createdAt);
    deadline.setHours(deadline.getHours() + hours[priority]);
    return deadline;
  }
}
